import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // totpState: null | { step: 'setup_totp'|'verify_totp', tempToken, totpUri? }
  const [totpState, setTotpState] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsAuthenticated(true);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      if (!response.success) {
        return { success: false, error: response.message };
      }

      if (response.next_step === 'setup_totp') {
        setTotpState({
          step: 'setup_totp',
          tempToken: response.data.temp_token,
          totpUri: response.data.totp_uri,
        });
        return { success: true, nextStep: 'setup_totp' };
      }

      if (response.next_step === 'verify_totp') {
        setTotpState({
          step: 'verify_totp',
          tempToken: response.data.temp_token,
        });
        return { success: true, nextStep: 'verify_totp' };
      }

      return { success: false, error: 'Unexpected server response' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const completeAuth = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setTotpState(null);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("authToken", userData.token);
  };

  const setupTotp = async (totpCode) => {
    try {
      const response = await authAPI.setupTotp(totpState.tempToken, totpCode);
      if (response.success) {
        completeAuth(response.data);
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const verifyTotp = async (totpCode) => {
    try {
      const response = await authAPI.verifyTotp(totpState.tempToken, totpCode);
      if (response.success) {
        completeAuth(response.data);
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setTotpState(null);
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
  };

  const value = {
    user,
    isAuthenticated,
    totpState,
    login,
    setupTotp,
    verifyTotp,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
